import {
  ActionConfirmationModal,
  Badge,
  Button,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import {
  retrieveMatterSettingsQueryOptions,
  useCreateMatterTagConnection,
  useDeleteMatterTagConnection,
} from "@/lib/data/react-query";
import { type Matter, type MatterTagConnection } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getMatterTagChipClassNames } from "@/lib/utils/matters/matter-tag-color";
import { useQuery } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

export default function MatterTagConnectionsSection({
  tagConnections,
  matterId,
  hasCreatePermission,
  hasDeletePermission,
  inline = false,
}: {
  tagConnections?: MatterTagConnection[];
  matterId: Matter["id"];
  hasCreatePermission?: boolean;
  hasDeletePermission?: boolean;
  inline?: boolean;
}) {
  const deleteMatterTagConnection = useDeleteMatterTagConnection();
  const createMatterTagConnection = useCreateMatterTagConnection();
  const [activeTagConnectionId, setActiveTagConnectionId] = useState<
    string | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingDeleteTagId, setPendingDeleteTagId] = useState<string | null>(
    null,
  );
  const { isPending: isDeletingTagConnection } = deleteMatterTagConnection;
  const { isPending: isCreatingTagConnection } = createMatterTagConnection;

  const { data: matterSettingsData, isLoading: isMatterTagsLoading } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );

  const existingTagConnections = useMemo(
    () => tagConnections ?? [],
    [tagConnections],
  );
  const activeTagConnection = useMemo(
    () =>
      existingTagConnections.find((tag) => tag.id === activeTagConnectionId),
    [activeTagConnectionId, existingTagConnections],
  );

  const handleAddTagConnection = async (id: string) => {
    if (!id) return;
    await createMatterTagConnection
      .mutateAsync({
        matterId,
        tagId: id,
      })
      .then(() => undefined);
  };

  const handleDeleteTagConnection = (
    tagConnection: Pick<MatterTagConnection, "id">,
  ) => {
    setPendingDeleteTagId(tagConnection.id);
    deleteMatterTagConnection.mutate(
      {
        id: tagConnection.id,
        matterId,
      },
      {
        onSettled: () => {
          setPendingDeleteTagId(null);
        },
      },
    );
    setActiveTagConnectionId(null);
  };

  const availableTags = useMemo(
    () =>
      (matterSettingsData?.data.tags ?? []).filter((tag) =>
        existingTagConnections.every(
          (tagConnection) => tag.id !== tagConnection.tagId,
        ),
      ),
    [existingTagConnections, matterSettingsData?.data.tags],
  );
  const isApplyDisabled =
    isMatterTagsLoading ||
    isCreatingTagConnection ||
    availableTags.length === 0;
  const applyTooltipMessage =
    availableTags.length === 0 ? "All tags already applied." : "";

  const tagItems = (
    <>
      {existingTagConnections.length > 0 ? (
        existingTagConnections.map((tagConnection) => (
          <div key={tagConnection.id} className="group relative">
            <Badge
              className={cn(
                "border px-2.5 py-0.5 text-xs font-semibold",
                hasDeletePermission ? "pr-7" : "pr-2.5",
                getMatterTagChipClassNames(tagConnection.tag.color),
              )}
            >
              {tagConnection.tag.label}
            </Badge>
            {hasDeletePermission && (
              <Button
                type="button"
                onClick={() => {
                  setActiveTagConnectionId(tagConnection.id);
                  setIsDeleting(true);
                }}
                variant="ghost"
                size="icon"
                className="bg-muted/30 hover:bg-muted/50 absolute top-1/2 right-1 h-5 w-5 -translate-y-1/2 rounded-full opacity-0 transition-opacity group-hover:opacity-80"
                disabled={isDeletingTagConnection}
                aria-label={`Remove ${tagConnection.tag.label}`}
              >
                {isDeletingTagConnection &&
                pendingDeleteTagId === tagConnection.id ? (
                  <LoadingSpinner className="h-3.5 w-3.5" />
                ) : (
                  <Trash2 className="text-destructive h-3 w-3" />
                )}
              </Button>
            )}
          </div>
        ))
      ) : inline ? null : (
        <p className="text-muted-foreground text-sm">No tags added yet.</p>
      )}
      {hasCreatePermission && (
        <Select
          value=""
          onValueChange={(value) => {
            void handleAddTagConnection(value);
          }}
          disabled={isApplyDisabled}
        >
          <TooltipWrapper message={applyTooltipMessage}>
            <div>
              <SelectTrigger
                className={cn(
                  "h-auto min-h-0 w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold",
                  isApplyDisabled &&
                    !isMatterTagsLoading &&
                    !isCreatingTagConnection
                    ? "text-muted-foreground"
                    : null,
                )}
              >
                <SelectValue
                  placeholder={
                    isMatterTagsLoading || isCreatingTagConnection
                      ? "Applying tag..."
                      : "Apply tag"
                  }
                />
                {(isMatterTagsLoading || isCreatingTagConnection) && (
                  <LoadingSpinner className="h-4 w-4" />
                )}
              </SelectTrigger>
            </div>
          </TooltipWrapper>
          <SelectContent>
            {isMatterTagsLoading && (
              <SelectItem value="null" disabled>
                Loading tags...
              </SelectItem>
            )}
            {!isMatterTagsLoading &&
              availableTags.map((tag) => (
                <SelectItem key={tag.id} value={tag.id}>
                  {tag.label}
                </SelectItem>
              ))}
            {!isMatterTagsLoading && availableTags.length === 0 && (
              <SelectItem value="null" disabled>
                No tags available
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      )}
    </>
  );

  if (inline) {
    return (
      <>
        {tagItems}
        <ActionConfirmationModal
          title={`Are you sure you want to delete this tag?`}
          open={isDeleting && !!activeTagConnection}
          setOpen={setIsDeleting}
          afterActionFn={() => {
            if (activeTagConnection) {
              handleDeleteTagConnection(activeTagConnection);
            }
          }}
        />
      </>
    );
  }

  return (
    <div>
      <Typography variant="subHeading">Tags</Typography>
      <div className="mt-2 flex flex-wrap items-center gap-2">{tagItems}</div>
      <ActionConfirmationModal
        title={`Are you sure you want to delete this tag?`}
        open={isDeleting && !!activeTagConnection}
        setOpen={setIsDeleting}
        afterActionFn={() => {
          if (activeTagConnection) {
            handleDeleteTagConnection(activeTagConnection);
          }
        }}
      />
    </div>
  );
}
